import { Map, List } from 'immutable';

import { TYPES } from './actions';
import Logger from '../../../lib/Logger';

function removeConversationFromPages(pages, conversationId, partnershipId) {
  if (!pages || !pages.get) return pages;

  const partnershipPages = pages.get(partnershipId);
  if (!partnershipPages) return pages;

  let updatedPages = pages;

  partnershipPages.forEach((orderPages, order) => {
    orderPages.forEach((limitPages, limit) => {
      limitPages.forEach((pageList, page) => {
        if (List.isList(pageList)) {
          const updatedList = pageList.filter((id) => {
            return id !== conversationId;
          });

          updatedPages = updatedPages.setIn(
            [partnershipId, order, limit, page],
            updatedList
          );
        }
      });
    });
  });

  return updatedPages;
}

export default function smsConversations(
  state = Map({
    isLoading: false,
    pages: {},
    total: undefined,
    deletedConversationId: null,
  }),
  action
) {
  Logger.log(
    'debug',
    `[smsConversations.reducers] smsConversations(%j, %j)`,
    state,
    action
  );

  switch (action.type) {
    case TYPES.LIST_REQUEST:
      return state.mergeDeep({
        isLoading: true,
      });

    case TYPES.LIST_SUCCESS:
      const existingResults = state.getIn(
        ['pages', action.partnershipId, action.order, action.limit, 1],
        List()
      );

      let newResults;
      if (action.page === 1) {
        newResults = List(action.result);
      } else {
        // Filter out duplicates before concatenating
        const newItems = action.result.filter(
          (id) => !existingResults.includes(id)
        );
        newResults = existingResults.concat(List(newItems));
      }

      return state
        .mergeDeep({
          isLoading: false,
          total: action.total,
          lastUpdated: action.receivedAt,
        })
        .setIn(
          ['pages', action.partnershipId, action.order, action.limit, 1],
          newResults
        );

    case TYPES.LIST_FAILURE:
      return state.mergeDeep({
        isLoading: false,
      });

    case TYPES.LIST_CLEAR:
      return state.merge({
        pages: {},
        total: undefined,
      });

    case TYPES.PATCH_REQUEST:
      return state.mergeDeep({
        form: {
          isSubmitting: true,
          success: null,
          errors: null,
        },
      });

    case TYPES.PATCH_SUCCESS:
      if (action.data?.clear_conversation) {
        const conversationId = action.id;
        let newState = state.mergeDeep({
          form: {
            isSubmitting: false,
            success: true,
            errors: null,
          },
          lastUpdated: action.receivedAt,
          deletedConversationId: conversationId,
        });

        const pages = newState.get('pages');
        if (pages) {
          const updatedPages = removeConversationFromPages(
            pages,
            conversationId,
            action.partnershipId
          );
          newState = newState.set('pages', updatedPages);

          const total = newState.get('total');
          if (total !== undefined && total > 0) {
            newState = newState.set('total', total - 1);
          }
        }

        return newState;
      }

      return state.mergeDeep({
        form: {
          isSubmitting: false,
          success: true,
          errors: null,
        },
        lastUpdated: action.receivedAt,
      });

    case TYPES.PATCH_FAILURE:
      return state
        .mergeDeep({
          form: {
            isSubmitting: false,
            success: false,
            errors: null,
          },
        })
        .setIn(['form', 'errors'], action.error);

    case TYPES.CLAIM_REQUEST:
      return state.mergeDeep({
        isLoading: true,
        form: {
          isSubmitting: true,
          success: null,
          errors: null,
        },
      });

    case TYPES.CLAIM_SUCCESS:
      const claimConversationId = action.id;
      const claimPartnershipId = action.partnershipId;
      let claimNewState = state.mergeDeep({
        isLoading: false,
        form: {
          isSubmitting: false,
          success: true,
          created_id: action.id,
          errors: null,
        },
        lastUpdated: action.receivedAt,
      });

      // Reorder the conversation in the list - move to first (desc) or last (asc)
      const claimPages = claimNewState.get('pages');
      if (claimPages) {
        const claimPartnershipPages = claimPages[claimPartnershipId];
        if (claimPartnershipPages) {
          Object.keys(claimPartnershipPages).forEach((order) => {
            const limitPages = claimPartnershipPages[order];
            if (!limitPages) return;

            Object.keys(limitPages).forEach((limit) => {
              const pagesList = limitPages[limit];
              if (!pagesList) return;

              const conversationList = pagesList['1'];
              if (!List.isList(conversationList)) return;

              const conversationIndex =
                conversationList.indexOf(claimConversationId);

              if (conversationIndex !== -1) {
                // Remove from current position and add to first/last
                const isDescending = order.includes('desc');
                const updatedList = conversationList
                  .delete(conversationIndex)
                  [isDescending ? 'unshift' : 'push'](claimConversationId);

                claimNewState = claimNewState.setIn(
                  ['pages', claimPartnershipId, order, limit, '1'],
                  updatedList
                );
              }
            });
          });
        }
      }

      return claimNewState;

    case TYPES.CLAIM_FAILURE:
      return state
        .mergeDeep({
          isLoading: false,
          form: {
            isSubmitting: false,
            success: false,
            errors: null,
          },
        })
        .setIn(['form', 'errors'], action.error);

    case TYPES.RESOLVE_REQUEST:
      return state.mergeDeep({
        isLoading: true,
        form: {
          isSubmitting: true,
          success: null,
          errors: null,
        },
      });

    case TYPES.RESOLVE_SUCCESS:
      return state.mergeDeep({
        isLoading: false,
        form: {
          isSubmitting: false,
          success: true,
          created_id: action.id,
          errors: null,
        },
        lastUpdated: action.receivedAt,
      });

    case TYPES.RESOLVE_FAILURE:
      return state
        .mergeDeep({
          isLoading: false,
          form: {
            isSubmitting: false,
            success: false,
            errors: null,
          },
        })
        .setIn(['form', 'errors'], action.error);

    case TYPES.REOPEN_REQUEST:
      return state.mergeDeep({
        isLoading: true,
        form: {
          isSubmitting: true,
          success: null,
          errors: null,
        },
      });

    case TYPES.REOPEN_SUCCESS:
      return state.mergeDeep({
        isLoading: false,
        form: {
          isSubmitting: false,
          success: true,
          created_id: action.id,
          errors: null,
        },
        lastUpdated: action.receivedAt,
      });

    case TYPES.REOPEN_FAILURE:
      return state
        .mergeDeep({
          isLoading: false,
          form: {
            isSubmitting: false,
            success: false,
            errors: null,
          },
        })
        .setIn(['form', 'errors'], action.error);

    case TYPES.MARK_AS_READ_REQUEST:
      return state.mergeDeep({
        isLoading: true,
        form: {
          isSubmitting: true,
          success: null,
          errors: null,
        },
      });

    case TYPES.MARK_AS_READ_SUCCESS:
      return state.mergeDeep({
        isLoading: false,
        form: {
          isSubmitting: false,
          success: true,
          created_id: action.id,
          errors: null,
        },
        lastUpdated: action.receivedAt,
      });

    case TYPES.MARK_AS_READ_FAILURE:
      return state
        .mergeDeep({
          isLoading: false,
          form: {
            isSubmitting: false,
            success: false,
            errors: null,
          },
        })
        .setIn(['form', 'errors'], action.error);

    case TYPES.UPDATE_FROM_SOCKET:
      const socketConversationId = action.conversationId;
      const socketPartnershipId = action.partnershipId;
      let socketNewState = state;

      const pages = state.get('pages');
      if (!pages) {
        return socketNewState.mergeDeep({
          lastUpdated: action.receivedAt,
        });
      }

      const partnershipPages = pages[socketPartnershipId];
      if (!partnershipPages) {
        return socketNewState.mergeDeep({
          lastUpdated: action.receivedAt,
        });
      }

      Object.keys(partnershipPages).forEach((order) => {
        const limitPages = partnershipPages[order];
        if (!limitPages) return;

        Object.keys(limitPages).forEach((limit) => {
          const pagesList = limitPages[limit];
          if (!pagesList) return;

          const conversationList = pagesList['1'];
          if (!List.isList(conversationList)) return;

          const conversationIndex =
            conversationList.indexOf(socketConversationId);

          if (conversationIndex === -1) {
            // New conversation - add to the list
            const isDescending = order.includes('desc');
            if (isDescending) {
              const updatedList = conversationList.unshift(socketConversationId);
              socketNewState = socketNewState.setIn(
                ['pages', socketPartnershipId, order, limit, '1'],
                updatedList
              );

              // Increment total count
              const currentTotal = socketNewState.get('total');
              if (currentTotal !== undefined) {
                socketNewState = socketNewState.set('total', currentTotal + 1);
              }
            }
          } else {
            // Conversation exists, move it to the appropriate position
            const isDescending = order.includes('desc');

            if (isDescending) {
              if (conversationIndex !== 0) {
                const updatedList = conversationList
                  .delete(conversationIndex)
                  .unshift(socketConversationId);

                socketNewState = socketNewState.setIn(
                  ['pages', socketPartnershipId, order, limit, '1'],
                  updatedList
                );
              }
            } else {
              const lastIndex = conversationList.size - 1;
              if (conversationIndex !== lastIndex) {
                const updatedList = conversationList
                  .delete(conversationIndex)
                  .push(socketConversationId);

                socketNewState = socketNewState.setIn(
                  ['pages', socketPartnershipId, order, limit, '1'],
                  updatedList
                );
              }
            }
          }
        });
      });

      return socketNewState.mergeDeep({
        lastUpdated: action.receivedAt,
      });

    case TYPES.ASSIGN_REQUEST:
      return state.mergeDeep({
        isLoading: true,
        form: {
          isSubmitting: true,
          success: null,
          errors: null,
        },
      });

    case TYPES.ASSIGN_SUCCESS:
      const assignConversationId = action.id;
      const assignPartnershipId = action.partnershipId;
      let assignNewState = state.mergeDeep({
        isLoading: false,
        form: {
          isSubmitting: false,
          success: true,
          created_id: action.id,
          errors: null,
        },
        lastUpdated: action.receivedAt,
      });

      // Reorder the conversation in the list - move to first (desc) or last (asc)
      const assignPages = assignNewState.get('pages');
      if (assignPages) {
        const assignPartnershipPages = assignPages[assignPartnershipId];
        if (assignPartnershipPages) {
          Object.keys(assignPartnershipPages).forEach((order) => {
            const limitPages = assignPartnershipPages[order];
            if (!limitPages) return;

            Object.keys(limitPages).forEach((limit) => {
              const pagesList = limitPages[limit];
              if (!pagesList) return;

              const conversationList = pagesList['1'];
              if (!List.isList(conversationList)) return;

              const conversationIndex =
                conversationList.indexOf(assignConversationId);

              if (conversationIndex !== -1) {
                // Remove from current position and add to first/last
                const isDescending = order.includes('desc');
                const updatedList = conversationList
                  .delete(conversationIndex)
                  [isDescending ? 'unshift' : 'push'](assignConversationId);

                assignNewState = assignNewState.setIn(
                  ['pages', assignPartnershipId, order, limit, '1'],
                  updatedList
                );
              }
            });
          });
        }
      }

      return assignNewState;

    case TYPES.ASSIGN_FAILURE:
      return state
        .mergeDeep({
          isLoading: false,
          form: {
            isSubmitting: false,
            success: false,
            errors: null,
          },
        })
        .setIn(['form', 'errors'], action.error);

    default:
      return state;
  }
}

Logger.log('silly', `smsConversations.reducers loaded.`);
